import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded'
import { Stack } from '@mui/material'
import { isIosApp } from 'webtonative'
import { request as requestTrackingConsent } from 'webtonative/ATTConsent'
import { login as loginApple } from 'webtonative/SocialLogin/apple'
import { login as loginFacebook } from 'webtonative/SocialLogin/facebook'
import { login as loginGoogle } from 'webtonative/SocialLogin/google'
import { useGlobalContext } from '../Contexts/GlobalContext'
import { loginSocial, upgradeGuestSocial } from '../globalHeader/functions/ams-functions'
import { analyticsManager } from '../globalHeader/services/analytics/AnalyticsManager'
import { reportSharedError, reportSharedWarning } from '../utils/errorReporting'
import { ServerErrors } from '../utils/serverErrors'
import GoogleIcon from './GoogleIcon'
import { beginNativeAuth, endNativeAuth } from './nativeAuthInFlight'
import SocialButton from './SocialButton'
import { NativeSsoProviders, SocialType } from './SocialLogin'

interface IProps {
    onSignUpSocial: (socialResponse: any) => void
    providers: NativeSsoProviders
}

// w2n callback payloads carry live credentials — DROP anything token-shaped
// before attaching a response to telemetry (naming the omitted keys keeps a
// missing-token failure distinguishable from a plain user cancel). Dropping
// rather than redacting in place matters: Sentry's server-side scrubber sees
// keys like `idToken` and replaces the WHOLE attribute with "[Filtered]", which
// is how every non-cancel Google failure on QA became undiagnosable.
const sanitizeW2nResponse = (response: unknown): Record<string, unknown> => {
    const entries = Object.entries((response ?? {}) as Record<string, unknown>)
    const kept = entries.filter(([key]) => !/token|credential|code|secret/i.test(key))
    const omitted = entries.filter(([key]) => /token|credential|code|secret/i.test(key)).map(([key]) => key)
    return { ...Object.fromEntries(kept), ...(omitted.length ? { omittedKeys: omitted.join(',') } : {}) }
}

const NativeSocialLogin: React.FC<IProps> = ({ onSignUpSocial, providers }) => {
    const { onLoginSuccessful, showSnackbar, isGuest, googleClientIdIos, googleClientIdAndroid } = useGlobalContext()

    // Native Google Sign-In mints the id_token for the platform OAuth client (iOS/Android), so the BE
    // must be told which one (`aud`). The web `googleClientId` isn't used on native at all.
    const nativeGoogleClientId = (isIosApp ? googleClientIdIos : googleClientIdAndroid) ?? ''

    const exchange = async (socialToken: string, accessToken: string, socialType: SocialType) => {
        if (isGuest) {
            return upgradeGuestSocial(socialToken, accessToken, socialType)
        }
        return loginSocial(socialToken, accessToken, socialType)
    }

    // `signupData` must match the shape SignUp.tsx reconstructs tokens from (web SDK response shape),
    // so the social-signup completion step works identically to the default (web) flow.
    const finalize = (
        signupData: any,
        data: any,
        socialType: SocialType,
        analyticsType: 'google' | 'facebook' | 'apple',
    ) => {
        if (data.error) {
            if (
                data.error.code === ServerErrors.USERNAME_IS_MANDATORY_FOR_FIRST_LOGIN ||
                data.error.code === ServerErrors.SOCIAL_LOGIN_MANDATORY_FIELDS_IS_MISSING
            ) {
                onSignUpSocial({ ...signupData, socialType, socialToken: data.error.details?.token })
            } else {
                // details may carry the social token — report only code/message/status.
                // status distinguishes "AMS rejected it" (4xx) from "never got an
                // answer" (undefined) — the SOMETHING_WENT_WRONG fallback hides that.
                reportSharedError(
                    'Native social auth rejected by server',
                    { code: data.error.code, message: data.error.message, status: data.error.status },
                    { provider: analyticsType, flow: 'native-login' },
                )
                // The ERROR dialog can't render here: the host sign-in dialog is forced
                // (negative timestamp) and DialogService renders only the top entry.
                showSnackbar?.(data.error.message)
            }
            return
        }
        analyticsManager.dispatchEvent('completeLogin', {
            authType: analyticsType,
            userId: data.user.id,
            userName: data.user.user_name,
        })
        onLoginSuccessful()
    }

    const handleGoogle = () => {
        // Suppress the resume-time session check that fires when the native sheet
        // closes — it would clobber this pending callback on the shared W2N
        // registry and race the login. See nativeAuthInFlight.ts.
        beginNativeAuth()
        loginGoogle({
            callback: (response) => {
                endNativeAuth()
                const { isSuccess, idToken } = response as { isSuccess?: boolean; idToken?: string }
                if (!isSuccess || !idToken) {
                    reportSharedWarning('Native Google auth unsuccessful', {
                        provider: 'google',
                        response: sanitizeW2nResponse(response),
                    })
                    return
                }
                const signupData = { credential: idToken, clientId: nativeGoogleClientId }
                exchange(idToken, nativeGoogleClientId, SocialType.GOOGLE)
                    .then((data) => finalize(signupData, data, SocialType.GOOGLE, 'google'))
                    .catch((err) => reportSharedError('Native Google auth failed', err, { flow: 'native-login' }))
            },
        })
    }

    const runFacebookLogin = () => {
        beginNativeAuth()
        loginFacebook({
            callback: (response) => {
                endNativeAuth()
                const { isSuccess, accessToken, userId } = response as {
                    isSuccess?: boolean
                    accessToken?: string
                    userId?: string
                }
                if (!isSuccess || !accessToken) {
                    reportSharedWarning('Native Facebook auth unsuccessful', {
                        provider: 'facebook',
                        response: sanitizeW2nResponse(response),
                    })
                    return
                }

                // Native FB SDK returns no signedRequest; userId is the closest equivalent for access_token
                // and is reused here so the exchange and signup-completion send the same value.
                const signupData = { authResponse: { accessToken, signedRequest: userId ?? '' } }
                exchange(accessToken, userId ?? '', SocialType.FACEBOOK)
                    .then((data) => finalize(signupData, data, SocialType.FACEBOOK, 'facebook'))
                    .catch((err) => reportSharedError('Native Facebook auth failed', err, { flow: 'native-login' }))
            },
        })
    }

    const handleFacebook = () => {
        // On iOS, without ATT consent the FB SDK falls back to Limited Login and returns an OIDC
        // authentication token (not the `EAA…` Graph access token the BE expects). Ask for tracking
        // consent first so the SDK can mint a full access token; proceed regardless of the answer.
        if (isIosApp) {
            // The ATT prompt is itself a native modal that backgrounds the app,
            // so arm the suppression before it too (runFacebookLogin re-arms).
            beginNativeAuth()
            requestTrackingConsent({ callback: () => runFacebookLogin() })
            return
        }
        runFacebookLogin()
    }

    const handleApple = () => {
        beginNativeAuth()
        loginApple({
            callback: (response) => {
                endNativeAuth()
                const { isSuccess, idToken, code } = response as {
                    isSuccess?: boolean
                    idToken?: string
                    code?: string
                }
                if (!isSuccess || !idToken) {
                    reportSharedWarning('Native Apple auth unsuccessful', {
                        provider: 'apple',
                        response: sanitizeW2nResponse(response),
                    })
                    return
                }
                const signupData = { authorization: { code: code ?? '' } }
                exchange(code ?? '', idToken, SocialType.APPLE)
                    .then((data) => finalize(signupData, data, SocialType.APPLE, 'apple'))
                    .catch((err) => reportSharedError('Native Apple auth failed', err, { flow: 'native-login' }))
            },
        })
    }

    return (
        <Stack gap={2} sx={{ width: '100%' }}>
            <Stack
                gap="24px"
                direction="row"
                justifyContent="space-around"
                sx={{
                    height: '70px',
                }}
            >
                {providers.apple && (
                    <SocialButton label="Apple" onClick={handleApple}>
                        <img style={{ width: '23px', height: '23px' }} src="/assets/images/apple.svg" />
                    </SocialButton>
                )}
                {providers.google && nativeGoogleClientId && (
                    <SocialButton label="Google" onClick={handleGoogle}>
                        <GoogleIcon size={23} />
                    </SocialButton>
                )}
                {providers.facebook && (
                    <SocialButton label="Facebook" onClick={handleFacebook}>
                        <FacebookRoundedIcon sx={{ width: '23px', height: '23px', fill: '#2374f2' }} />
                    </SocialButton>
                )}
            </Stack>
        </Stack>
    )
}

NativeSocialLogin.displayName = 'NativeSocialLogin'

export default NativeSocialLogin
